import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import type {
  FeedingType,
  AppointmentFeedingType,
  MealData,
  FeedData,
} from "@/types/nutrition";
import {
  useAppointmentFeedingType,
  useFeedingTypes,
  useDietRecall,
  useMealPlanData,
} from "@/hooks/useNutritionEvaluation";
import { useConfirmUnsaved } from "@/hooks/useUnsavedChanges";

// =============================================================================
// Context Types
// =============================================================================

interface NutritionEvaluationContextType {
  appointmentId: string;
  patientId: string;
  isCompleted: boolean;

  // Feeding type state
  feedingTypes: FeedingType[];
  currentFeedingType: AppointmentFeedingType | null;
  isTubeFeeding: boolean;
  isLoadingFeedingTypes: boolean;
  isFeedingTypeReady: boolean; // true once appointment feeding type query has settled
  feedingTypeTemplate: string;

  // Tab state
  activeTab: NutritionTab;
  setActiveTab: (tab: NutritionTab) => void;

  // Meal plan state (for Create Meal Plan tab)
  selectedMealPlanIndex: number;
  setSelectedMealPlanIndex: (index: number) => void;

  // Diet Recall / Meal Plan data
  dietRecallData: MealData | FeedData | null;
  isDietRecallLoading: boolean;
  mealPlanData: (MealData | FeedData | null)[];
  isMealPlanLoading: boolean[];

  // Expanded meal time state for diet recall view
  expandedMealTimes: Set<string>;
  toggleMealTimeExpanded: (mealTimeId: string) => void;

  // Nutrient calculations (matching Flutter's controller methods)
  carbsGramsToKcal: (grams: number) => number;
  proteinsGramsToKcal: (grams: number) => number;
  fatsGramsToKcal: (grams: number) => number;
  fiberGramsToKcal: (grams: number) => number;
}

export type NutritionTab =
  | "nutrition-profile"
  | "diet-recall"
  | "set-target"
  | "meal-plan"
  | "monitoring"
  | "follow-up";

const VALID_NUTRITION_TABS: Set<string> = new Set<string>([
  "nutrition-profile",
  "diet-recall",
  "set-target",
  "meal-plan",
  "monitoring",
  "follow-up",
]);

const NutritionEvaluationContext =
  createContext<NutritionEvaluationContextType | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

interface NutritionEvaluationProviderProps {
  children: React.ReactNode;
  appointmentId: string;
  patientId: string;
  isCompleted?: boolean;
}

// =============================================================================
// Provider Component
// =============================================================================

export const NutritionEvaluationProvider: React.FC<
  NutritionEvaluationProviderProps
> = ({ children, appointmentId, patientId, isCompleted = false }) => {
  // Tab state — synced with URL search param so it survives refresh
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: NutritionTab = VALID_NUTRITION_TABS.has(
    searchParams.get("ntab") || ""
  )
    ? (searchParams.get("ntab") as NutritionTab)
    : "nutrition-profile";
  const confirmUnsaved = useConfirmUnsaved();
  const setActiveTab = useCallback(
    (tab: NutritionTab) => {
      // Same-tab clicks shouldn't trigger the unsaved-changes dialog.
      if (tab === activeTab) return;
      confirmUnsaved(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("ntab", tab);
            return next;
          },
          { replace: true }
        );
      });
    },
    [activeTab, setSearchParams, confirmUnsaved]
  );

  // Meal plan state
  const [selectedMealPlanIndex, setSelectedMealPlanIndex] = useState(0);

  // Expanded meal times for diet recall view (matching Flutter's currentOpenMealTime)
  const [expandedMealTimes, setExpandedMealTimes] = useState<Set<string>>(
    new Set()
  );

  const toggleMealTimeExpanded = useCallback((mealTimeId: string) => {
    setExpandedMealTimes((prev) => {
      const next = new Set(prev);
      if (next.has(mealTimeId)) {
        next.delete(mealTimeId);
      } else {
        next.add(mealTimeId);
      }
      return next;
    });
  }, []);

  // Fetch feeding types
  const { data: feedingTypes = [], isLoading: isLoadingFeedingTypes } =
    useFeedingTypes();
  const {
    data: currentFeedingType,
    isLoading: isLoadingAppointmentFeedingType,
    isFetched: isFeedingTypeFetched,
  } = useAppointmentFeedingType(appointmentId);

  // Feeding type is "ready" once the appointment feeding type query has completed
  // This matches Flutter's check: if (controller.selectedFeedingTypeTemplate.value.isEmpty) { retry... }
  const isFeedingTypeReady =
    isFeedingTypeFetched && !isLoadingAppointmentFeedingType;

  // Determine if tube feeding is active and get template
  const feedingTypeTemplate = useMemo(() => {
    return (
      currentFeedingType?.feeding_type_details?.template ||
      "regular_meal_template"
    );
  }, [currentFeedingType]);

  const isTubeFeeding = feedingTypeTemplate === "feed_template";

  // Fetch diet recall data based on feeding type
  // IMPORTANT: Only fetch once feeding type is determined to avoid fetching with wrong template
  const { data: dietRecallData = null, isLoading: isDietRecallLoading } =
    useDietRecall(appointmentId, isTubeFeeding);

  // Fetch meal plan data for all 3 options (only after feeding type is ready).
  // Tube feeding only supports Plan A — skip 1 and 2 to avoid 404s.
  const { data: mealPlan0 = null, isLoading: isMealPlan0Loading } =
    useMealPlanData(appointmentId, 0, isTubeFeeding);
  const { data: mealPlan1 = null, isLoading: isMealPlan1Loading } =
    useMealPlanData(appointmentId, 1, isTubeFeeding, !isTubeFeeding);
  const { data: mealPlan2 = null, isLoading: isMealPlan2Loading } =
    useMealPlanData(appointmentId, 2, isTubeFeeding, !isTubeFeeding);

  const mealPlanData = useMemo(
    () => [mealPlan0, mealPlan1, mealPlan2],
    [mealPlan0, mealPlan1, mealPlan2]
  );
  const isMealPlanLoading = useMemo(
    () => [isMealPlan0Loading, isMealPlan1Loading, isMealPlan2Loading],
    [isMealPlan0Loading, isMealPlan1Loading, isMealPlan2Loading]
  );

  // Nutrient calculation functions matching Flutter's controller
  const carbsGramsToKcal = useCallback((grams: number) => grams * 4, []);
  const proteinsGramsToKcal = useCallback((grams: number) => grams * 4, []);
  const fatsGramsToKcal = useCallback((grams: number) => grams * 9, []);
  const fiberGramsToKcal = useCallback((grams: number) => grams * 2, []);

  const value = useMemo<NutritionEvaluationContextType>(
    () => ({
      appointmentId,
      patientId,
      isCompleted,
      feedingTypes,
      currentFeedingType: currentFeedingType || null,
      isTubeFeeding,
      isLoadingFeedingTypes,
      isFeedingTypeReady,
      feedingTypeTemplate,
      activeTab,
      setActiveTab,
      selectedMealPlanIndex,
      setSelectedMealPlanIndex,
      dietRecallData,
      isDietRecallLoading,
      mealPlanData,
      isMealPlanLoading,
      expandedMealTimes,
      toggleMealTimeExpanded,
      carbsGramsToKcal,
      proteinsGramsToKcal,
      fatsGramsToKcal,
      fiberGramsToKcal,
    }),
    [
      appointmentId,
      patientId,
      isCompleted,
      feedingTypes,
      currentFeedingType,
      isTubeFeeding,
      isLoadingFeedingTypes,
      isFeedingTypeReady,
      feedingTypeTemplate,
      activeTab,
      selectedMealPlanIndex,
      dietRecallData,
      isDietRecallLoading,
      mealPlanData,
      isMealPlanLoading,
      expandedMealTimes,
      toggleMealTimeExpanded,
      carbsGramsToKcal,
      proteinsGramsToKcal,
      fatsGramsToKcal,
      fiberGramsToKcal,
    ]
  );

  return (
    <NutritionEvaluationContext.Provider value={value}>
      {children}
    </NutritionEvaluationContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

export const useNutritionEvaluationContext = () => {
  const context = useContext(NutritionEvaluationContext);
  if (!context) {
    throw new Error(
      "useNutritionEvaluationContext must be used within NutritionEvaluationProvider"
    );
  }
  return context;
};
